// Autonomous wedged-session recovery for the web player (#383).
//
// A silently stalled HLS session (the server transcode worker stops producing
// segments) leaves the playhead frozen while the video element is *not* paused
// and hls.js merely keeps retrying fragment loads. That produces no hls.js
// fatal error and no position regression, so the existing event-driven
// recovery never fires and the player sits in "Reconnecting…" forever.
//
// This module is the pure decision core for a no-progress watchdog: given a
// periodic observation of the playhead and a small amount of memory, it decides
// whether to do nothing, note healthy progress, or escalate to a session
// handoff. It is intentionally side-effect free so it can be unit-tested
// without rendering the player; PlayerPage owns the timer and the actions.
import {
  absolutePositionMS,
  durationMS,
  ms,
  negativeInfinityWallClockMS,
  rawBufferedAheadMS,
  rawVideoPositionMS,
  wallClockMS,
  type AbsolutePositionMS,
  type DurationMS,
  type RawVideoPositionMS,
  type WallClockMS,
} from "@/pages/playerTimeline";

export const STALL_WATCHDOG_TICK_MS = 2_000;
// How long the playhead may stay frozen (while eligible) before we escalate.
export const STALL_RECOVERY_AFTER_MS = 12_000;
// Minimum spacing between watchdog-driven recoveries. This is the guard against
// the historical stall-recovery teardown loop (#230): we reconnect slowly, not
// every few seconds.
export const STALL_RECOVERY_COOLDOWN_MS = 20_000;
// Forward movement smaller than this is treated as "not really advancing"
// (timer jitter / sub-frame drift), not as progress.
export const STALL_PROGRESS_TOLERANCE_MS = 250;
// Sustained healthy progress re-arms the per-incident recovery budget so a
// later, independent stall can recover again instead of being permanently
// capped within one long-lived player instance.
export const RUNTIME_RECOVERY_BUDGET_RESET_MS = 30_000;
// Buffer-ahead-of-playhead above this is treated as "data side healthy",
// which inhibits session-replacement escalation. Native HLS players (iPad
// Safari) cap their buffer by spec — once full, fetching stops by design, so
// `bufferAdvanced=false` is the *normal* state of a healthy session with a
// full buffer, not a wedge signal. Frozen playhead with a full buffer is a
// client decoder issue (#401); replacing the session can't fix it but
// destroys the running ffmpeg producer and breaks the user-gesture chain
// across navigate(), landing the user in autoplay-blocked recovery.
export const HEALTHY_BUFFER_AHEAD_MS = 10_000;

// Native HLS owns ordinary stall recovery. Only the hls.js/MSE engine may use
// this watchdog's bounded delivery handoff after the pure decision core proves
// simultaneous playhead and low-buffer starvation.
export function stallWatchdogMayRecoverDelivery(nativeHlsPlayback: boolean): boolean {
  return !nativeHlsPlayback;
}

export type StallWatchdogConfig = {
  recoverAfterMS: DurationMS;
  cooldownMS: DurationMS;
  progressToleranceMS: DurationMS;
  budgetResetMS: DurationMS;
  healthyBufferAheadMS: DurationMS;
};

export const DEFAULT_STALL_WATCHDOG_CONFIG: StallWatchdogConfig = {
  recoverAfterMS: durationMS(STALL_RECOVERY_AFTER_MS),
  cooldownMS: durationMS(STALL_RECOVERY_COOLDOWN_MS),
  progressToleranceMS: durationMS(STALL_PROGRESS_TOLERANCE_MS),
  budgetResetMS: durationMS(RUNTIME_RECOVERY_BUDGET_RESET_MS),
  healthyBufferAheadMS: durationMS(HEALTHY_BUFFER_AHEAD_MS),
};

export type StallWatchdogInput = {
  // True only when playback should be advancing: playback has started, the
  // video is not paused, the user has not paused, and no seek/renegotiation/
  // handoff/next-episode transition is in flight.
  eligible: boolean;
  positionMS: AbsolutePositionMS;
  // Raw video-element playhead position (ms). This is intentionally separate
  // from absolute playback position so buffer-ahead checks cannot subtract a
  // raw buffered range from the absolute media timeline (#407).
  videoPositionMS: RawVideoPositionMS;
  // Furthest buffered position (ms). This is the "data is still arriving"
  // signal that separates a genuine wedge (dead worker: playhead frozen AND
  // buffer not growing) from a merely slow/constrained startup (playhead may
  // sit while the buffer keeps filling). Without it the watchdog would tear
  // down healthy-but-slow streams (cf. #290).
  bufferedEndMS: RawVideoPositionMS;
  nowMS: WallClockMS;
};

export type StallWatchdogMemory = {
  lastPositionMS: AbsolutePositionMS;
  lastBufferedEndMS: RawVideoPositionMS;
  lastAdvanceAtMS: WallClockMS;
  lastRecoveryAtMS: WallClockMS;
  // Whether the current eligible run has established a baseline observation.
  // The first eligible sample (and the first after any ineligible gap, e.g. a
  // seek) only sets the baseline — it is never treated as progress or a freeze,
  // since the absolute playhead value jumps.
  baselined: boolean;
  // Whether the playhead has advanced at least once since playback started.
  // The watchdog only arms after real forward progress, so it never acts during
  // startup/resume buffering (where the playhead legitimately sits at 0).
  hasProgressed: boolean;
};

export type StallWatchdogAction = "idle" | "progress" | "recover";

export type StallWatchdogResult = {
  action: StallWatchdogAction;
  // When true, the caller should clear its runtime-recovery attempt counter.
  resetRecoveryBudget: boolean;
  memory: StallWatchdogMemory;
};

export function initialStallWatchdogMemory(): StallWatchdogMemory {
  // lastRecoveryAtMS starts at -Infinity so the cooldown never blocks the
  // first recovery (no recovery has happened yet).
  return {
    lastPositionMS: absolutePositionMS(0),
    lastBufferedEndMS: rawVideoPositionMS(0),
    lastAdvanceAtMS: wallClockMS(0),
    lastRecoveryAtMS: negativeInfinityWallClockMS(),
    baselined: false,
    hasProgressed: false,
  };
}

export function evaluateStallWatchdog(
  input: StallWatchdogInput,
  memory: StallWatchdogMemory,
  config: StallWatchdogConfig = DEFAULT_STALL_WATCHDOG_CONFIG,
): StallWatchdogResult {
  const { eligible, positionMS, videoPositionMS, bufferedEndMS, nowMS } = input;

  // Not a stall candidate (paused, seeking, handoff in flight, pre-start).
  // Drop the baseline so the next eligible sample re-establishes it (the
  // playhead may have jumped), and hold the advance clock at "now" so the
  // watchdog cannot fire immediately when these transient states clear.
  if (!eligible) {
    return {
      action: "idle",
      resetRecoveryBudget: false,
      memory: { ...memory, baselined: false, lastAdvanceAtMS: nowMS },
    };
  }

  // First eligible sample (startup, or first after an ineligible gap like a
  // seek): only establish the baseline. The absolute playhead value jumps here,
  // so it is neither progress nor a freeze.
  if (!memory.baselined) {
    return {
      action: "idle",
      resetRecoveryBudget: false,
      memory: {
        ...memory,
        baselined: true,
        lastPositionMS: positionMS,
        lastBufferedEndMS: bufferedEndMS,
        lastAdvanceAtMS: nowMS,
      },
    };
  }

  // Progress = the playhead advanced OR the buffer is still filling. A genuine
  // wedge (dead worker) freezes both; a slow/constrained stream keeps filling
  // the buffer even when the playhead briefly waits, so it must not escalate.
  const playheadAdvanced = ms(positionMS) > ms(memory.lastPositionMS) + ms(config.progressToleranceMS);
  const bufferAdvanced = ms(bufferedEndMS) > ms(memory.lastBufferedEndMS) + ms(config.progressToleranceMS);
  if (playheadAdvanced || bufferAdvanced) {
    const sustainedProgress = ms(nowMS) - ms(memory.lastRecoveryAtMS) > ms(config.budgetResetMS);
    return {
      action: "progress",
      resetRecoveryBudget: sustainedProgress,
      memory: {
        ...memory,
        // The watchdog only ever arms after the playhead itself moves; buffer
        // growth alone (slow startup) keeps playback healthy but does not arm.
        hasProgressed: memory.hasProgressed || playheadAdvanced,
        lastPositionMS: absolutePositionMS(Math.max(ms(memory.lastPositionMS), ms(positionMS))),
        lastBufferedEndMS: rawVideoPositionMS(Math.max(ms(memory.lastBufferedEndMS), ms(bufferedEndMS))),
        lastAdvanceAtMS: nowMS,
      },
    };
  }

  // Data side healthy: enough buffered ahead of the playhead that the session
  // is plainly receiving data even if the buffer isn't actively growing right
  // now. Frozen playhead here is a client decoder problem, not a server one;
  // do not escalate to session replacement (#401).
  if (ms(rawBufferedAheadMS(bufferedEndMS, videoPositionMS)) >= ms(config.healthyBufferAheadMS)) {
    return { action: "idle", resetRecoveryBudget: false, memory };
  }

  // Both the playhead and the buffer are frozen while eligible, and buffer is
  // not healthy. Escalate only once playback has actually progressed (never
  // during startup), it has been frozen long enough, and we are off the
  // recovery cooldown.
  const frozenForMS = ms(nowMS) - ms(memory.lastAdvanceAtMS);
  const offCooldown = ms(nowMS) - ms(memory.lastRecoveryAtMS) >= ms(config.cooldownMS);
  if (memory.hasProgressed && frozenForMS >= ms(config.recoverAfterMS) && offCooldown) {
    return {
      action: "recover",
      resetRecoveryBudget: false,
      memory: { ...memory, lastRecoveryAtMS: nowMS },
    };
  }

  return { action: "idle", resetRecoveryBudget: false, memory };
}
